import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';

/** One "needs attention" item — see backend/utils/adminAttention.js. */
export interface AttentionItem {
  /** The ADMIN_SETTINGS id it opens. */
  setting: string;
  /** Optional tab path below the setting (ADMIN_SUBTABS), e.g. 'erasure' or 'sms/connection'. */
  tab?: string;
  count: number;
  tone: 'danger' | 'warn' | 'approval';
  title: string;
  detail: string;
}

/**
 * GET /api/admin/attention. Polled once a minute and refetched whenever the
 * Admin path changes (AdminModule calls `refetch`), so a queue cleared on
 * one screen stops being counted by the time you're back on the home page.
 * Failure is silent — the strip and rail counts are a convenience.
 */
export function useAdminAttention() {
  return useQuery({
    queryKey: keys.admin.attention(),
    queryFn: () => api.get<{ items: AttentionItem[] }>('/admin/attention'),
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

/** Total count per setting, and the most serious tone among its items. */
export function attentionBySetting(items: AttentionItem[] | undefined) {
  const order = { danger: 0, approval: 1, warn: 2 } as const;
  const map = new Map<string, { count: number; tone: AttentionItem['tone'] }>();
  for (const i of items ?? []) {
    const prev = map.get(i.setting);
    map.set(i.setting, {
      count: (prev?.count ?? 0) + i.count,
      tone: prev && order[prev.tone] < order[i.tone] ? prev.tone : i.tone,
    });
  }
  return map;
}

/** Tailwind classes for a count pill in a given tone. */
export const ATTENTION_PILL: Record<AttentionItem['tone'], string> = {
  danger: 'bg-danger-soft text-danger-ink',
  warn: 'bg-warn-soft text-warn-ink',
  approval: 'bg-info-soft text-info-ink',
};
